<template>
  <v-container class="px-5 my-5">
    <v-row>
      <v-col cols="12">
        <div class="headline font-weight-bold mb-4">
          {{ $t("notifications.title") }}
        </div>
        <v-breadcrumbs
          divider=">"
          :items="breadcrumbItems"
          class="pa-0"
        ></v-breadcrumbs>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-lg">
          <v-card-title class="text-overline">
            {{ $t("notifications.card_title") }}
          </v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="form.valid" lazy-validation>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.title"
                    :rules="rules.titleRules"
                    :hint="$t('notifications.form.inputs.title.hint')"
                    :label="$t('notifications.form.inputs.title.text')"
                    class="rounded-lg"
                    prepend-inner-icon="title"
                    outlined
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.description"
                    :rules="rules.descriptionRules"
                    :hint="$t('notifications.form.inputs.description.hint')"
                    :label="$t('notifications.form.inputs.description.text')"
                    class="rounded-lg"
                    rows="1"
                    prepend-inner-icon="comment"
                    outlined
                    required
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="6">
                  <!-- <v-autocomplete
                    v-model="form.redirect"
                    :items="itemsSections"
                    item-text="text"
                    item-value="value"
                    :label="$t('notifications.form.inputs.redirect.text')"
                    class="rounded-lg"
                    prepend-inner-icon="navigate_next"
                    small-chips
                    outlined
                    clearable
                  ></v-autocomplete> -->
                  <v-text-field
                    v-model="form.redirect"
                    :label="$t('notifications.form.inputs.redirect.text')"
                    class="rounded-lg"
                    prepend-inner-icon="navigate_next"
                    outlined
                  >
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="form.language"
                    :items="languages"
                    item-text="name"
                    item-value="code"
                    :label="$t('notifications.form.inputs.language.text')"
                    class="rounded-lg"
                    :rules="rules.languageRules"
                    prepend-inner-icon="language"
                    small-chips
                    outlined
                    clearable
                  ></v-autocomplete>
                </v-col>

                <v-col cols="12">
                  <v-card
                    outlined
                    class="rounded-lg"
                    style="border: 2px solid #b3b3b3"
                  >
                    <p
                      class="text-overline pl-4 pt-2 grey--text text--darken-2"
                    >
                      <v-icon size="16" class="pr-2">send</v-icon>
                      {{ $t("notifications.form.inputs.send_methods.title") }}
                    </p>
                    <v-row>
                      <v-col cols="12" md="6" class="d-flex justify-center">
                        <v-checkbox
                          v-model="sentAll"
                          :label="
                            $t(
                              'notifications.form.inputs.send_methods.globally'
                            )
                          "
                          @click="handleSentCheckboxes(1)"
                          color="#2661ff"
                        ></v-checkbox>
                      </v-col>
                      <v-col cols="12" md="6" class="d-flex justify-center">
                        <v-checkbox
                          v-model="sentSpecific"
                          :label="
                            $t(
                              'notifications.form.inputs.send_methods.specific.title'
                            )
                          "
                          @click="handleSentCheckboxes(2)"
                          color="#2661ff"
                        ></v-checkbox>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>

                <v-col cols="12" v-if="sentSpecific">
                  <v-card
                    class="mx-auto rounded-lg"
                    outlined
                    style="border-left: 8px solid #2661ff !important;"
                  >
                    <div class="text-overline pl-4 pb-4 pt-2">
                      {{
                        $t(
                          "notifications.form.inputs.send_methods.specific.sent_to"
                        )
                      }}
                    </div>
                    <v-row class="px-4">
                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="form.sentOrganizations"
                          :items="organizations"
                          item-value="id"
                          item-text="name"
                          :label="
                            $t(
                              'notifications.form.inputs.send_methods.specific.organizations'
                            )
                          "
                          class="rounded-lg"
                          :rules="rules.organizationsRule"
                          prepend-inner-icon="corporate_fare"
                          multiple
                          outlined
                          clearable
                          small-chips
                          @change="handleOrganizationsChange"
                        >
                        </v-autocomplete>
                        <!-- Error message for first autocomplete -->
                        <p
                          class="subtitle-1 text-center"
                          style="color: #0d47a1;"
                          v-if="form.sentOrganizations.length === 0"
                        >
                          {{
                            $t(
                              "notifications.form.inputs.send_methods.specific.error_organizations"
                            )
                          }}
                        </p>
                      </v-col>
                      <v-col
                        cols="12"
                        md="6"
                        v-if="form.sentOrganizations.length > 0"
                      >
                        <v-autocomplete
                          v-model="form.sentUsers"
                          :items="usersByOrganizations"
                          :item-text="
                            user =>
                              user.person
                                ? `${user.person.first_name} ${user.person.last_name}`
                                : user.email
                          "
                          :rules="rules.usersRule"
                          item-value="id"
                          :label="
                            $t(
                              'notifications.form.inputs.send_methods.specific.users'
                            )
                          "
                          class="rounded-lg"
                          prepend-inner-icon="group"
                          outlined
                          multiple
                          small-chips
                          clearable
                        >
                          <template v-slot:prepend-item>
                            <v-list-item ripple @click="toggleSelectAllUsers">
                              <v-list-item-action>
                                <v-checkbox
                                  v-model="isSelectedAll"
                                ></v-checkbox>
                              </v-list-item-action>
                              <v-list-item-content>
                                <v-list-item-title
                                  >Select All</v-list-item-title
                                >
                              </v-list-item-content>
                            </v-list-item>
                            <v-divider class="mt-2"></v-divider>
                          </template>
                        </v-autocomplete>
                        <!-- Error message for second autocomplete -->
                        <p
                          class="subtitle-1 text-center"
                          style="color: #0d47a1;"
                          v-if="form.sentUsers.length === 0"
                        >
                          {{
                            $t(
                              "notifications.form.inputs.send_methods.specific.error_users"
                            )
                          }}
                        </p>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>

                <v-col cols="12" class="d-flex justify-end px-8 pb-6">
                  <v-btn
                    color="blue-grey"
                    large
                    class="mx-8 white--text"
                    :loading="form.resetButton.loading"
                    :disabled="form.resetButton.disabled"
                    @click="reset"
                  >
                    {{ this.$t("actions.clean") }}
                    <v-icon class="ml-2 mb-1">restart_alt</v-icon>
                  </v-btn>
                  <v-btn
                    color="#2661FF"
                    large
                    class="white--text"
                    :loading="form.confirmButton.loading"
                    :disabled="form.confirmButton.disabled"
                    @click.prevent="handleCreateNotification"
                  >
                    {{ this.$t("actions.create") }}
                    <v-icon class="ml-2 mb-1">add_circle</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-lg">
          <v-card-title class="text-overline">
            {{ $t("notifications.table.title") }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="notificationsTable.search"
                  @keyup.enter="getNotificationsData"
                  @click:append="
                    notificationsTable.search ? getNotificationsData : null
                  "
                  :label="$t('actions.search')"
                  outlined
                  clearable
                  class="rounded-lg"
                  :placeholder="$t('form_listing.search_by_name')"
                  prepend-inner-icon="search"
                />
              </v-col>
            </v-row>
            <v-data-table
              :loading="notificationsTable.loading"
              :headers="notificationsTable.headers"
              :items="notificationsTable.items"
              :options="notificationsTable.pagination"
              :footer-props="{ 'items-per-page-options': [5, 10, 15, 20] }"
              :items-per-page="notificationsTable.pagination.itemsPerPage"
              :server-items-length="notificationsTable.total"
              @update:options="notificationsTable.pagination = $event"
            >
              <!-- SHOWS NO DATA -->
              <template v-slot:no-data>
                <div class="text-center pa-5 my-5">
                  <v-img
                    :src="require('@/assets/svg/box_empty.svg')"
                    height="150"
                    max-height="150px"
                    aspect-ratio="1"
                    contain
                  />
                  <div class="grey--text">
                    {{ $t("common.no_data") }}
                  </div>
                </div>
              </template>
              <template
                v-if="notificationsTable.items.length"
                v-slot:body="{ items }"
              >
                <tbody>
                  <tr v-for="item in items" :key="item.id">
                    <td class="text-no-wrap">
                      {{
                        item.user.person
                          ? `${item.user.person.first_name} ${item.user.person.last_name}`
                          : item.user.email
                      }}
                    </td>
                    <td class="text-no-wrap">
                      {{ item.title }}
                    </td>
                    <td>
                      {{ item.text }}
                    </td>
                    <td>
                      {{ item.link }}
                    </td>
                    <td>
                      {{ item.language }}
                    </td>
                    <td>
                      {{ item.read }}
                    </td>
                    <td>
                      {{ item.created_at | moment("DD/MM/YYY") }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from "vuex";
import { organizationService } from "@/services/organization.service";
import { userService } from "@/services/user.service";
import moment from "moment";

export default {
  name: "Notifications",
  layout: "Dashboard",
  data() {
    return {
      breadcrumbItems: [
        {
          text: this.$t("notifications.breadcrumbItems.comunications"),
          disabled: true
        },
        {
          text: this.$t("notifications.title"),
          disabled: false
        }
      ],
      organizations: [],
      usersByOrganizations: [],
      sentAll: false,
      sentSpecific: false,
      isSelectedAll: false,
      form: {
        valid: true,
        title: null,
        description: null,
        redirect: null,
        language: null,
        sentOrganizations: [],
        sentUsers: [],
        type: "INFORMATIVE",
        resetButton: { loading: false, disabled: false },
        confirmButton: { loading: false, disabled: false }
      },
      rules: {
        titleRules: [
          v => !!v || this.$t("notifications.form.rules.title.required")
        ],
        descriptionRules: [
          v => !!v || this.$t("notifications.form.rules.description.required"),
          v =>
            (v && v.length >= 10) ||
            this.$t("notifications.form.rules.description.length")
        ],
        languageRules: [
          v => !!v || this.$t("notifications.form.rules.language.required")
        ],
        organizationsRule: [
          v => !!v || this.$t("notifications.form.rules.organizations.required")
        ],
        usersRule: [
          v => !!v || this.$t("notifications.form.rules.users.required")
        ]
      },
      notificationsTable: {
        loading: false,
        items: [],
        search: null,
        total: 0,
        pagination: {
          sortBy: ["created_at"],
          sortDesc: [true],
          itemsPerPage: 10
        },
        headers: [
          {
            text: this.$t("notifications.table.headers.user"),
            align: "center",
            sortable: true,
            value: "user.person.first_name",
            class: "text-no-wrap blue--text text--darken-4"
          },
          {
            text: this.$t("notifications.table.headers.title"),
            align: "center",
            sortable: true,
            value: "title",
            class: "text-no-wrap blue--text text--darken-4"
          },
          {
            text: this.$t("notifications.table.headers.description"),
            align: "center",
            sortable: false,
            value: "text",
            class: "text-no-wrap blue--text text--darken-4"
          },
          {
            text: "Link",
            align: "center",
            sortable: false,
            value: "link",
            class: "text-no-wrap blue--text text--darken-4"
          },
          {
            text: this.$t("notifications.table.headers.language"),
            align: "center",
            sortable: false,
            value: "language",
            class: "text-no-wrap blue--text text--darken-4"
          },
          {
            text: this.$t("notifications.table.headers.read"),
            align: "center",
            sortable: false,
            value: "read",
            class: "text-no-wrap blue--text text--darken-4"
          },
          {
            text: this.$t("notifications.table.headers.created_at"),
            align: "center",
            sortable: true,
            value: "created_at",
            class: "text-no-wrap blue--text text--darken-4"
          }
        ]
      }
    };
  },
  async created() {
    await this.getAllOrganizations();
    await this.getNotificationsData();
  },
  computed: {
    ...mapState({
      languages: ({ auth: { locales } }) => locales,
      allUsers: ({ user: { allUsers } }) => allUsers,
      notifications: ({ communicationByOrg: { notifications } }) =>
        notifications,
      notificationsTotal: ({ communicationByOrg: { notificationsTotal } }) =>
        notificationsTotal
    }),
    isNotificationCreatable() {
      if (this.$refs.form.validate()) {
        if (this.sentSpecific) {
          // Only enable creation if both specifics autocompletes have a value
          return (
            this.form.sentOrganizations.length > 0 &&
            this.form.sentUsers.length > 0
          );
        } else if (this.sentAll) {
          // Enable creation if globally is selected
          return true;
        }
      }
      return false;
    },
    selectAllUsers() {
      return this.form.sentUsers.length === this.usersByOrganizations.length;
    }
  },
  watch: {
    "notificationsTable.pagination": {
      async handler() {
        await this.getNotificationsData();
      },
      deep: true
    }
  },
  methods: {
    ...mapActions({
      getAllOrganizations: "organization/getAll",
      getAllUsers: "user/getAllUsers",
      createNotification: "communicationByOrg/createNotification",
      getNotifications: "communicationByOrg/getNotifications"
    }),
    async getAllOrganizations() {
      try {
        const {
          data: organizations
        } = await organizationService.getAllOrganizations();

        this.organizations = organizations;
      } catch (error) {
        console.log(error);
      }
    },

    async getNotificationsData() {
      this.notificationsTable.loading = true;

      let { sortBy, sortDesc = false } = this.notificationsTable.pagination;
      const { page, itemsPerPage } = this.notificationsTable.pagination;

      if (Array.isArray(sortBy)) {
        [sortBy] = sortBy;
      }
      if (Array.isArray(sortDesc)) {
        [sortDesc] = sortDesc;
      }

      await this.getNotifications({
        search: this.notificationsTable.search,
        page,
        rowsPerPage: itemsPerPage,
        sortBy: sortBy || "id",
        descending: sortDesc
      });

      this.notificationsTable.items = this.notifications;
      this.notificationsTable.total = parseInt(this.notificationsTotal, 10);

      this.notificationsTable.loading = false;
    },
    handleSentCheckboxes(checkboxNumber) {
      this.form.sentOrganizations = [];
      this.form.sentUsers = [];
      if (checkboxNumber === 1) {
        this.sentSpecific = false;
      } else if (checkboxNumber === 2) {
        this.sentAll = false;
      }
    },
    toggleSelectAllUsers() {
      if (this.selectAllUsers) {
        this.form.sentUsers = [];
        this.isSelectedAll = false;
      } else {
        this.isSelectedAll = true;
        this.form.sentUsers = this.usersByOrganizations.map(user => user.id);
      }
    },
    async handleCreateNotification() {
      this.form.confirmButton.loading = true;
      if (this.isNotificationCreatable) {
        if (this.sentAll) {
          await this.getAllUsers();

          /* Sent to every user */
          this.allUsers.forEach(({ id }) => {
            this.form.sentUsers.push(id);
          });
        }

        await this.createNotification({
          title: this.form.title,
          text: this.form.description,
          link: this.form.redirect,
          users: this.form.sentUsers,
          language: this.form.language,
          type: this.form.type
        });

        this.reset();
        await this.getNotificationsData();
      } else {
        this.$noty.error(this.$t("notifications.messages.warning"));
      }

      this.form.confirmButton.loading = false;
    },
    reset() {
      this.form.resetButton.loading = true;
      this.$refs.form.reset();
      this.form.resetButton.loading = false;
    },
    async handleOrganizationsChange() {
      const {
        data: usersByOrganizations
      } = await userService.getUsersByOrganization({
        organizations: this.form.sentOrganizations
      });

      this.usersByOrganizations = [];

      usersByOrganizations.forEach(userOrganization => {
        if (userOrganization.user) {
          this.usersByOrganizations.push(userOrganization.user);
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped></style>
